import React, { useState, useEffect, useCallback, useMemo } from "react";
import convert from "convert-units";
import { debounce } from "lodash";

import { injectIntl, intlShape } from "react-intl";
import { messages } from "../../i18n-messages";

import {
    DialogBox,
    DialogBoxFooterType,
    RadioButtonGroup,
    RadioButton,
    NumericInput,
    SelectInput,
    Loader,
} from "~/core";

import { mapToolsActions } from "~/map";
import { ISelectOption, IZoneGridOptions, Toolset } from "@ai360/core";
import { ACTIVE_YN } from "~/core/picklist";
import { logFirebaseEvent } from "~/utils/firebase";

import { eventsModels as em, eventsSelectors } from "~/recs-events";

import "./place-zone-grid-modal.css";
import { ActionPanelModuleList } from "~/action-panel";
import { ToolsetPayload } from "~/map/components/map-tools/components/map-tools.4x";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import {
    useLoginUserPreferences,
    useUpdateLoginUserPreferences,
} from "~/core/components/login-user-preferences";

const ZONE_GRID = "ZoneGrid";
const AREA_DIMENSIONS = "areaDimensions";

interface IPlaceZoneGridModalProps {
    activeModule: (typeof ActionPanelModuleList)[keyof typeof ActionPanelModuleList];
    activeType: string;
    clearGridOnCancel: boolean;
    defaultZoneUserGridSettings: IZoneGridOptions;
    intl: intlShape;
    onClose: (isAction: boolean) => void;
    onUpdateZones: () => void;
    toolsetPayload: ToolsetPayload;
    unitOptionsArea: ISelectOption<string>[];
    unitOptionsLength: ISelectOption<string>[];
    zoneGridOptions: IZoneGridOptions;
}

export const PlaceZoneGridModal_ = (props: IPlaceZoneGridModalProps) => {
    const dispatch = useAppDispatch();

    const onEnableGridToolset = useCallback(
        (zoneGridOrientationSettings?: any) => {
            if (zoneGridOrientationSettings) {
                dispatch(
                    mapToolsActions.setZoneGridOrientationSettings(zoneGridOrientationSettings)
                );
            }
            dispatch(mapToolsActions.setActiveToolsetOnly(Toolset.ZONE_GRID));
        },
        [dispatch]
    );

    const setZoneGridOrientationSettings = useCallback(
        (zoneGridOrientationSettings: any) => {
            dispatch(mapToolsActions.setZoneGridOrientationSettings(zoneGridOrientationSettings));
        },
        [dispatch]
    );

    const onSetZoneGridStandardUnitSettings = useMemo(
        () =>
            debounce(
                (gridSettings: any) =>
                    dispatch(mapToolsActions.setZoneGridStandardUnitSettings(gridSettings)),
                1000,
                { leading: true }
            ),
        [dispatch]
    );

    const onUpdateZoneGridOptions = useCallback(
        (zoneGridOptions: any) => {
            dispatch(mapToolsActions.onUpdateZoneGridOptions(zoneGridOptions));
        },
        [dispatch]
    );

    const onEnableZoneToolset = useCallback(
        (
            createGridZones: boolean,
            clearGrid: boolean,
            toolset: Toolset,
            toolsetPayload: ToolsetPayload
        ) => {
            dispatch(
                mapToolsActions.setActiveToolset(toolset, {
                    ...toolsetPayload,
                    createGridZones,
                    clearGrid,
                })
            );
        },
        [dispatch]
    );

    const getDefaultAreaUnitGuid = (unitOptions) => {
        const acresOption = unitOptions.find((o) => o.label === "ac");
        if (acresOption != null) {
            return acresOption.value;
        }
        return unitOptions[0].value;
    };

    const getDefaultLengthUnitGuid = (unitOptions) => {
        const ftOption = unitOptions.find((o) => o.label === "ft");
        if (ftOption != null) {
            return ftOption.value;
        }
        return unitOptions[0].value;
    };

    const { gridMaxAreaAcres, gridMinAreaAcres } = useAppSelector((state) =>
        eventsSelectors.getModuleState(state)
    );

    const [lastSelectedAreaUnitGuid, setLastSelectedAreaUnitGuid] = useState<string>(
        props.zoneGridOptions.areaDimension === em.AreaDimension.AREA
            ? props.zoneGridOptions.gridUnitGuid
            : props.unitOptionsArea && props.unitOptionsArea.length > 0
            ? getDefaultAreaUnitGuid(props.unitOptionsArea)
            : null
    );

    const [lastSelectedLengthUnitGuid, setLastSelectedLengthUnitGuid] = useState<string>(
        props.zoneGridOptions.areaDimension === em.AreaDimension.DIMENSION
            ? props.zoneGridOptions.gridUnitGuid
            : props.unitOptionsLength && props.unitOptionsLength.length > 0
            ? getDefaultLengthUnitGuid(props.unitOptionsLength)
            : null
    );

    const [originalGridSettings, setOriginalGridSettings] = useState<any>(null);

    const { loginUserPreferences, isLoading } = useLoginUserPreferences(ZONE_GRID, [
        AREA_DIMENSIONS,
    ]);
    const { savePreference } = useUpdateLoginUserPreferences(ZONE_GRID);

    const handleSave = async () => {
        const areaDimensionsData = {
            lastSelectedAreaUnitGuid,
            lastSelectedLengthUnitGuid,
            zoneGridOptions: {
                areaAcres: props.zoneGridOptions.areaAcres,
                areaDimension: props.zoneGridOptions.areaDimension,
                gridSize: props.zoneGridOptions.gridSize,
                gridUnitGuid: props.zoneGridOptions.gridUnitGuid,
                width: props.zoneGridOptions.width,
                height: props.zoneGridOptions.height,
            },
        };

        await savePreference(AREA_DIMENSIONS, JSON.stringify(areaDimensionsData));
    };

    useEffect(() => {
        if (loginUserPreferences?.areaDimensions) {
            try {
                const areaDimensionsData = JSON.parse(loginUserPreferences.areaDimensions);

                if (areaDimensionsData.lastSelectedAreaUnitGuid) {
                    setLastSelectedAreaUnitGuid(areaDimensionsData.lastSelectedAreaUnitGuid);
                }

                if (areaDimensionsData.lastSelectedLengthUnitGuid) {
                    setLastSelectedLengthUnitGuid(areaDimensionsData.lastSelectedLengthUnitGuid);
                }

                if (areaDimensionsData.zoneGridOptions) {
                    const updatedOptions = { ...props.zoneGridOptions };

                    if (areaDimensionsData.zoneGridOptions.areaAcres !== null) {
                        updatedOptions.areaAcres = areaDimensionsData.zoneGridOptions.areaAcres;
                    }

                    if (areaDimensionsData.zoneGridOptions.areaDimension !== null) {
                        updatedOptions.areaDimension =
                            areaDimensionsData.zoneGridOptions.areaDimension;
                    }

                    if (areaDimensionsData.zoneGridOptions.gridSize !== null) {
                        updatedOptions.gridSize = areaDimensionsData.zoneGridOptions.gridSize;
                    }

                    if (areaDimensionsData.zoneGridOptions.gridUnitGuid !== null) {
                        updatedOptions.gridUnitGuid =
                            areaDimensionsData.zoneGridOptions.gridUnitGuid;
                    }

                    if (areaDimensionsData.zoneGridOptions.width !== null) {
                        updatedOptions.width = areaDimensionsData.zoneGridOptions.width;
                    }

                    if (areaDimensionsData.zoneGridOptions.height !== null) {
                        updatedOptions.height = areaDimensionsData.zoneGridOptions.height;
                    }

                    onUpdateZoneGridOptions(updatedOptions);
                    const gridDimensions =
                        updatedOptions.areaDimension === em.AreaDimension.AREA
                            ? {
                                  gridSize: updatedOptions.gridSize,
                                  gridUnitGuid: updatedOptions.gridUnitGuid,
                              }
                            : {
                                  height: updatedOptions.height,
                                  width: updatedOptions.width,
                                  gridUnitGuid: updatedOptions.gridUnitGuid,
                              };
                    updateGridDimensions(gridDimensions);
                }
            } catch (error) {
                console.error("Error parsing areaDimensions:", error);
            }
        }
    }, [isLoading, loginUserPreferences?.areaDimensions, onUpdateZoneGridOptions]);

    useEffect(() => {
        onUpdateZoneGridOptions({ ...props.zoneGridOptions });
        onEnableGridToolset({
            offsetX: props.zoneGridOptions.offsetX,
            offsetY: props.zoneGridOptions.offsetY,
            rotation: props.zoneGridOptions.rotation,
            areaAcres: props.zoneGridOptions.areaAcres,
            cellHeightFt: props.zoneGridOptions.cellHeightFt,
            cellWidthFt: props.zoneGridOptions.cellWidthFt,
        });
        setOriginalGridSettings({
            offsetX: props.zoneGridOptions.offsetX,
            offsetY: props.zoneGridOptions.offsetY,
            rotation: props.zoneGridOptions.rotation,
            areaAcres: props.zoneGridOptions.areaAcres,
            cellHeightFt: props.zoneGridOptions.cellHeightFt,
            cellWidthFt: props.zoneGridOptions.cellWidthFt,
        });
    }, [onEnableGridToolset, onUpdateZoneGridOptions]);

    const getAreaUnitFromGuid = useCallback(
        (gridUnitGuid) => {
            const { unitOptionsArea } = props;

            if (unitOptionsArea?.length === 0) {
                return "ac";
            }

            if (gridUnitGuid == null) {
                gridUnitGuid = getDefaultAreaUnitGuid(unitOptionsArea);
            }

            const fromUnitOption = unitOptionsArea.find((o) => o.value === gridUnitGuid);
            if (fromUnitOption == null) {
                if (unitOptionsArea.find((o) => o.value === gridUnitGuid) == null) {
                    console.error("Invalid `gridUnitGuid`", gridUnitGuid);
                }
                return "ac";
            }
            const m = fromUnitOption.label.match(/^(sq )?(.+)$/);
            return m[1] === "sq " ? `${m[2]}2` : m[0];
        },
        [props.unitOptionsArea]
    );

    const getLengthUnitFromGuid = useCallback(
        (gridUnitGuid) => {
            const { unitOptionsLength } = props;

            if (unitOptionsLength?.length === 0) {
                return "ft";
            }

            if (gridUnitGuid == null) {
                gridUnitGuid = getDefaultLengthUnitGuid(unitOptionsLength);
            }

            const fromUnitOption = unitOptionsLength.find((o) => o.value === gridUnitGuid);
            if (fromUnitOption == null) {
                if (unitOptionsLength.find((o) => o.value === gridUnitGuid) == null) {
                    console.error("Invalid `gridUnitGuid`", gridUnitGuid);
                }
                return "ft";
            }
            return fromUnitOption.label;
        },
        [props.unitOptionsLength]
    );

    const getCurrentAreaAc = useCallback(
        (zoneGridOptions) => {
            if (zoneGridOptions == null) {
                zoneGridOptions = props.zoneGridOptions;
            }
            const { areaDimension, height, gridSize, width } = zoneGridOptions;

            if (areaDimension === em.AreaDimension.AREA) {
                const fromUnit = getAreaUnitFromGuid(null);
                return convert(gridSize).from(fromUnit).to("ac");
            }

            console.assert(areaDimension === em.AreaDimension.DIMENSION);
            const fromUnit = getLengthUnitFromGuid(null);

            const widthFt = convert(width).from(fromUnit).to("ft");
            const heightFt = convert(height).from(fromUnit).to("ft");

            return convert(widthFt * heightFt)
                .from("ft2")
                .to("ac");
        },
        [props.zoneGridOptions, getAreaUnitFromGuid, getLengthUnitFromGuid]
    );

    const getIsAreaOutOfRange = useCallback(
        (zoneGridOptions) => {
            if (zoneGridOptions == null) {
                zoneGridOptions = props.zoneGridOptions;
            }

            const { areaDimension, height, gridSize, width } = zoneGridOptions;
            if (
                areaDimension === em.AreaDimension.AREA &&
                (typeof gridSize !== "number" || isNaN(gridSize))
            ) {
                return true;
            }
            if (
                areaDimension === em.AreaDimension.DIMENSION &&
                (typeof height !== "number" ||
                    typeof width !== "number" ||
                    isNaN(height) ||
                    isNaN(width))
            ) {
                return true;
            }

            const specifiedAreaAc = getCurrentAreaAc(zoneGridOptions);
            return specifiedAreaAc < gridMinAreaAcres || specifiedAreaAc > gridMaxAreaAcres;
        },
        [gridMaxAreaAcres, gridMinAreaAcres, props.zoneGridOptions, getCurrentAreaAc]
    );

    const getLastSelectedAreaUnit = useCallback(() => {
        return {
            gridUnitGuid: lastSelectedAreaUnitGuid,
            toUnit: getAreaUnitFromGuid(lastSelectedAreaUnitGuid),
        };
    }, [lastSelectedAreaUnitGuid, getAreaUnitFromGuid]);

    const getLastSelectedLengthUnit = useCallback(() => {
        return {
            gridUnitGuid: getDefaultLengthUnitGuid(props.unitOptionsLength),
            toUnit: "ft",
        };
    }, [props.unitOptionsLength]);

    const getInstructionsPart = useCallback(() => {
        const { formatMessage } = props.intl;
        return (
            <div className="instructions">
                <div>{formatMessage(messages.zoneGridModalInstructions)}</div>
                <div>{formatMessage(messages.zoneGridModalInstructions2)}</div>
            </div>
        );
    }, [props.intl]);

    const updateGridDimensions = useMemo(
        () =>
            debounce((newProps) => {
                const { zoneGridOptions } = props;

                onUpdateZoneGridOptions(newProps);

                const newZoneGridModel = { ...zoneGridOptions, ...newProps };

                if (newZoneGridModel.areaDimension === em.AreaDimension.AREA) {
                    const fromUnit = getAreaUnitFromGuid(newZoneGridModel.gridUnitGuid);
                    onSetZoneGridStandardUnitSettings({
                        areaAcres: convert(newZoneGridModel.gridSize).from(fromUnit).to("ac"),
                    });
                    return;
                }

                console.assert(newZoneGridModel.areaDimension === em.AreaDimension.DIMENSION);
                const fromUnit = getLengthUnitFromGuid(null);
                onSetZoneGridStandardUnitSettings({
                    cellHeightFt: convert(newZoneGridModel.height).from(fromUnit).to("ft"),
                    cellWidthFt: convert(newZoneGridModel.width).from(fromUnit).to("ft"),
                });
            }, 700),
        [
            props.zoneGridOptions,
            onSetZoneGridStandardUnitSettings,
            onUpdateZoneGridOptions,
            getAreaUnitFromGuid,
            getLengthUnitFromGuid,
        ]
    );

    const setGridUnit = useCallback(
        (gridUnitGuid) => {
            const { zoneGridOptions } = props;
            let { height, gridSize, width } = zoneGridOptions;

            if (zoneGridOptions.areaDimension === em.AreaDimension.AREA) {
                const fromUnit = getAreaUnitFromGuid(null);
                const toUnit = getAreaUnitFromGuid(gridUnitGuid);
                gridSize = convert(gridSize).from(fromUnit).to(toUnit);

                setLastSelectedAreaUnitGuid(gridUnitGuid);
                updateGridDimensions({ gridSize, gridUnitGuid });
                return;
            }

            console.assert(zoneGridOptions.areaDimension === em.AreaDimension.DIMENSION);
            const fromUnit = getLengthUnitFromGuid(null);
            const toUnit = getLengthUnitFromGuid(gridUnitGuid);
            height = convert(height).from(fromUnit).to(toUnit);
            width = convert(width).from(fromUnit).to(toUnit);

            setLastSelectedLengthUnitGuid(gridUnitGuid);
            updateGridDimensions({ height, width, gridUnitGuid });
        },
        [props.zoneGridOptions, getAreaUnitFromGuid, getLengthUnitFromGuid, updateGridDimensions]
    );

    const getDimensionsPart = useCallback(() => {
        const { zoneGridOptions, unitOptionsArea, unitOptionsLength } = props;
        const { formatMessage } = props.intl;

        const areaOutOfRange = getIsAreaOutOfRange(null);
        const outOfRangeMsg = !areaOutOfRange ? null : (
            <div className="out-of-range-msg">
                {formatMessage(messages.zoneGridAreaOutOfRange, {
                    gridMinAreaAcres,
                    gridMaxAreaAcres,
                })}
            </div>
        );

        if (zoneGridOptions.areaDimension === em.AreaDimension.AREA) {
            return (
                <div className="dimensions">
                    <div className="dim-input">
                        <NumericInput
                            onChange={(v, f, numVal) =>
                                numVal != zoneGridOptions.gridSize &&
                                updateGridDimensions({ gridSize: numVal })
                            }
                            containerClassNames={[{ "form-input-error": areaOutOfRange }]}
                            placeholderText={formatMessage(messages.zoneGridSizeLblTxt)}
                            precision={9}
                            scale={3}
                            value={zoneGridOptions.gridSize}
                        />
                        <SelectInput
                            clearable={false}
                            optionIsHiddenKey={ACTIVE_YN}
                            onChange={(val) => setGridUnit(val)}
                            placeholderText={formatMessage(messages.zoneGridUnitLblTxt)}
                            options={unitOptionsArea}
                            value={zoneGridOptions.gridUnitGuid}
                        />
                    </div>
                    {outOfRangeMsg}
                </div>
            );
        }
        console.assert(zoneGridOptions.areaDimension === em.AreaDimension.DIMENSION);
        return (
            <div className="dimensions">
                <div className="dim-input">
                    <div>
                        <NumericInput
                            onChange={(v, f, numVal) =>
                                numVal != zoneGridOptions.width &&
                                updateGridDimensions({ width: numVal })
                            }
                            containerClassNames={[{ "form-input-error": areaOutOfRange }]}
                            placeholderText={formatMessage(messages.zoneGridWidthLblTxt)}
                            precision={9}
                            scale={2}
                            value={zoneGridOptions.width}
                        />
                    </div>
                    <div>
                        <NumericInput
                            onChange={(v, f, numVal) =>
                                numVal != zoneGridOptions.height &&
                                updateGridDimensions({ height: numVal })
                            }
                            containerClassNames={[{ "form-input-error": areaOutOfRange }]}
                            placeholderText={formatMessage(messages.zoneGridHeightLblTxt)}
                            precision={9}
                            scale={2}
                            value={zoneGridOptions.height}
                        />
                    </div>
                    <SelectInput
                        clearable={false}
                        disabled={true}
                        optionIsHiddenKey={ACTIVE_YN}
                        onChange={(val) => setGridUnit(val)}
                        placeholderText={formatMessage(messages.zoneGridUnitLblTxt)}
                        options={unitOptionsLength}
                        value={zoneGridOptions.gridUnitGuid}
                    />
                </div>
                {outOfRangeMsg}
            </div>
        );
    }, [
        props.zoneGridOptions,
        props.unitOptionsArea,
        props.unitOptionsLength,
        getIsAreaOutOfRange,
        updateGridDimensions,
        setGridUnit,
    ]);

    const getRadioBtnGroupPart = useCallback(() => {
        const { zoneGridOptions } = props;
        const { formatMessage } = props.intl;

        return (
            <div className="radio-btn-groups">
                <RadioButtonGroup
                    className="radio-grp"
                    selectedValue={zoneGridOptions.areaDimension}
                    afterOnChange={(val) => setAreaDimension(val)}
                >
                    <div className="radio-grp-lbl">
                        {formatMessage(messages.zoneGridMethodLblTxt)}
                    </div>
                    <RadioButton
                        value={em.AreaDimension.AREA}
                        label={formatMessage(messages.zoneGridAreaLblTxt)}
                    />
                    <RadioButton
                        value={em.AreaDimension.DIMENSION}
                        label={formatMessage(messages.zoneGridDimensionsLblTxt)}
                    />
                </RadioButtonGroup>
            </div>
        );
    }, [props.zoneGridOptions, props.intl]);

    const getStandardGridDimensions = useCallback(() => {
        const { zoneGridOptions } = props;
        const { areaDimension, height, gridSize, width } = zoneGridOptions;

        if (areaDimension === em.AreaDimension.AREA) {
            const fromUnit = getAreaUnitFromGuid(null);
            const gridSizeFtSq = convert(gridSize).from(fromUnit).to("ft2");
            return { gridSizeFtSq };
        }

        console.assert(areaDimension === em.AreaDimension.DIMENSION);
        const heightFt = height;
        const widthFt = width;
        return { heightFt, widthFt };
    }, [props.zoneGridOptions, getAreaUnitFromGuid]);

    const setAreaDimension = useCallback(
        (newAreaDimension) => {
            const { zoneGridOptions } = props;
            if (zoneGridOptions.areaDimension === newAreaDimension) {
                return;
            }

            let { height, gridSize, width } = zoneGridOptions;

            if (newAreaDimension === em.AreaDimension.AREA) {
                // Changing from width/height to area.
                const { gridUnitGuid, toUnit } = getLastSelectedAreaUnit();

                const { heightFt, widthFt } = getStandardGridDimensions();
                gridSize = convert(heightFt * widthFt)
                    .from("ft2")
                    .to(toUnit);

                updateGridDimensions({
                    areaDimension: newAreaDimension,
                    gridSize,
                    gridUnitGuid,
                });
                return;
            }

            // Changing from area to width/height
            console.assert(newAreaDimension === em.AreaDimension.DIMENSION);

            const { gridUnitGuid, toUnit } = getLastSelectedLengthUnit();
            const { gridSizeFtSq } = getStandardGridDimensions();
            const widthHeightRatio = Math.pow(width / height, 0.5);
            height = convert(Math.pow(gridSizeFtSq, 0.5) / widthHeightRatio)
                .from("ft")
                .to(toUnit);
            width = convert(Math.pow(gridSizeFtSq, 0.5) * widthHeightRatio)
                .from("ft")
                .to(toUnit);
            updateGridDimensions({
                areaDimension: newAreaDimension,
                gridUnitGuid,
                height,
                width,
            });
        },
        [
            props.zoneGridOptions,
            getLastSelectedAreaUnit,
            getStandardGridDimensions,
            getLastSelectedLengthUnit,
            updateGridDimensions,
        ]
    );

    const isValid = useMemo(() => {
        const { zoneGridOptions } = props;
        const { areaDimension, height, gridSize, width } = zoneGridOptions;

        if (areaDimension === em.AreaDimension.AREA) {
            return typeof gridSize === "number" && !isNaN(gridSize) && gridSize > 0;
        }

        if (areaDimension === em.AreaDimension.DIMENSION) {
            return (
                typeof height === "number" &&
                typeof width === "number" &&
                !isNaN(height) &&
                !isNaN(width) &&
                height > 0 &&
                width > 0
            );
        }

        return false;
    }, [props.zoneGridOptions]);

    const handleClose = useCallback(
        async (createZones: boolean) => {
            if (createZones) {
                await handleSave();
            }

            let toolSet: Toolset;
            if (props.activeType?.toLowerCase().includes("sampling")) {
                toolSet = Toolset.SAMPLING;
            } else if (props.activeType?.toLowerCase().includes("analysis")) {
                toolSet = Toolset.ANALYSIS;
            } else {
                toolSet = Toolset.ZONE_EDIT;
            }

            if (!createZones) {
                onEnableZoneToolset(false, true, toolSet, props.toolsetPayload);
            } else {
                onEnableZoneToolset(true, true, toolSet, props.toolsetPayload);
            }
            props.onClose(false);
        },
        [props.activeType, props.toolsetPayload, props.onClose, onEnableZoneToolset, handleSave]
    );

    return (
        <DialogBox
            draggable
            forceOverflow
            isOpen
            unrestricted
            action={props.intl.formatMessage(messages.zoneGridCreateZonesBtnTxt)}
            className="zone-grid-modal"
            footerType={DialogBoxFooterType.ACTION_CANCEL}
            actionDisabled={!isValid}
            onAction={() => {
                logFirebaseEvent(
                    `zones_grid_${
                        props.zoneGridOptions.areaDimension === "A" ? "area" : "dimensions"
                    }`
                );
                handleClose(true);
            }}
            onClose={() => {
                setZoneGridOrientationSettings(originalGridSettings);
                handleClose(false);
            }}
            title={props.intl.formatMessage(messages.zoneGridModalTitle)}
        >
            {isLoading ? <Loader className="grid-zone-loader" /> : null}
            {getRadioBtnGroupPart()}
            {getDimensionsPart()}
            {getInstructionsPart()}
        </DialogBox>
    );
};

export const PlaceZoneGridModal = injectIntl(PlaceZoneGridModal_);
